// Coach CRM API wrapper (backend/app/routers/crm.py). One small function per endpoint.
// Pages call useCrm(); the functions are built once per API_URL. Errors are axios errors: a duplicate
// add is err.response.status === 409 with data { detail: 'exists', relationship_id }.
import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const cfg = (params) => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  ...(params ? { params } : {}),
});
const data = (p) => p.then((r) => r.data);

export function createCrm(API_URL) {
  const base = `${API_URL}/crm`;
  return {
    // relationships. params: { q, division, status, shared: 'mine'|'shared'|'all' }
    listRelationships: (params = {}) => data(axios.get(`${base}/relationships`, cfg(params))),
    createRelationship: (body) => data(axios.post(`${base}/relationships`, body, cfg())),
    getRelationship: (id) => data(axios.get(`${base}/relationships/${id}`, cfg())),
    patchRelationship: (id, fields) => data(axios.patch(`${base}/relationships/${id}`, fields, cfg())),
    deleteRelationship: (id) => data(axios.delete(`${base}/relationships/${id}`, cfg())),
    // shared coach identity
    patchCoach: (coachId, fields) => data(axios.patch(`${base}/coaches/${coachId}`, fields, cfg())),
    // activity + text handoff
    listActivity: (id) => data(axios.get(`${base}/relationships/${id}/activity`, cfg())),
    addActivity: (id, kind, body) => data(axios.post(`${base}/relationships/${id}/activity`, { kind, body }, cfg())),
    startText: (id, { templateId, body } = {}) => data(axios.post(
      `${base}/relationships/${id}/text`, { template_id: templateId ?? null, body: body ?? null }, cfg())),
    confirmText: (activityId, sent) => data(axios.post(`${base}/activity/${activityId}/confirm`, { sent }, cfg())),
    // templates
    listTemplates: () => data(axios.get(`${base}/templates`, cfg())),
    createTemplate: (body) => data(axios.post(`${base}/templates`, body, cfg())),
    patchTemplate: (id, fields) => data(axios.patch(`${base}/templates/${id}`, fields, cfg())),
    deleteTemplate: (id) => data(axios.delete(`${base}/templates/${id}`, cfg())),
    // colleague sharing (staff only)
    listColleagues: () => data(axios.get(`${base}/colleagues`, cfg())),
    listShares: () => data(axios.get(`${base}/shares`, cfg())),
    createShare: (granteeUserId, relationshipId = null) => data(axios.post(
      `${base}/shares`, { grantee_user_id: granteeUserId, relationship_id: relationshipId }, cfg())),
    deleteShare: (id) => data(axios.delete(`${base}/shares/${id}`, cfg())),
    // admin reassignment (admin only)
    adminOwners: () => data(axios.get(`${base}/admin/owners`, cfg())),
    adminReassign: (fromUserId, toUserId) => data(axios.post(
      `${base}/admin/reassign`, { from_user_id: fromUserId, to_user_id: toUserId }, cfg())),
  };
}

export function useCrm() {
  const { API_URL } = useAuth();
  return useMemo(() => createCrm(API_URL), [API_URL]);
}
